<template>
  <!-- Component: LayoutHeader -->
  <div ref="headerEl" class="sticky top-0 z-[1000]">
    <StwMenubar :model="items" breakpoint="1279px">
      <template #start>
        <RouterLink to="/">
          <div
            class="flex cursor-pointer items-center gap-2 py-[3px] pr-2 font-semibold"
          >
            <img
              class="h-[40px] w-auto"
              :src="PLATFORM_LOGO_URL"
              alt="OSCE-AI Platform: AI Patient Connect"
            />
          </div>
        </RouterLink>
      </template>

      <template #item="{ item, props: itemProps }">
        <a v-bind="itemProps.action">
          <span v-bind="itemProps.icon" :class="item.icon" />
          <span v-bind="itemProps.label">{{ item.label }}</span>
        </a>
      </template>

      <template #end>
        <div class="flex items-center gap-1 xl:gap-2">
          <LocaleSwitcher />

          <div v-if="!user" class="flex gap-2">
            <StwButton size="sm" @click="handleAuth">
              <i class="pi pi-user" />
              <span class="hidden xl:block">
                {{ t('siteHeader.loginRegister') }}
              </span>
            </StwButton>
          </div>

          <div v-else class="flex items-center gap-2">
            <StwButton
              size="sm"
              :aria-label="t('siteHeader.logout')"
              @click="handleLogout"
            >
              <i class="pi pi-sign-out" />
              <!-- Icon only below 1536px, so the menu fits on one line. -->
              <span class="hidden 2xl:block">{{ t('siteHeader.logout') }}</span>
            </StwButton>

            <!-- Everyone gets the default mark — avatars aren't user-editable
                 here (Kelvin, 2026-09-29), so a previously uploaded one is
                 ignored too. -->
            <RouterLink
              class="flex items-center rounded-md p-0.5 transition hover:![background-color:var(--stw-glass-bg-hover)]"
              to="/settings"
            >
              <img
                class="h-8 w-8 rounded-md object-cover ring-1 [--tw-ring-color:var(--stw-glass-border)]"
                :src="PLATFORM_MARK_URL"
                :alt="t('siteHeader.userAvatarAlt')"
              />
            </RouterLink>
          </div>
        </div>
      </template>
    </StwMenubar>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import LocaleSwitcher from '@/components/LocaleSwitcher.vue';
import { StwButton, StwMenubar } from '@/components/stw';
import { PLATFORM_LOGO_URL, PLATFORM_MARK_URL } from '@/constants/branding';
import { useAuthStore } from '@/stores/auth';
import { useStwToast } from '@/utils/stwToast';

const router = useRouter();
const authStore = useAuthStore();
const toast = useStwToast();
const route = useRoute();
const { t } = useI18n();

const { user } = storeToRefs(authStore);

// Published as --site-header-h so other sticky bars (the live-session
// header) can sit right under this one instead of behind it.
const headerEl = ref<HTMLElement>();
useResizeObserver(headerEl, ([entry]) => {
  document.documentElement.style.setProperty(
    '--site-header-h',
    `${entry.target.getBoundingClientRect().height}px`
  );
});

// Faculty role (USER_ROLE.PROF = 2) — see @stw/types. Admin has deliberately
// no nav entry, for admins or anyone else — it's reached by typing /admin
// directly, gated by the is_admin route guard in main.ts.
const isFaculty = computed(() => user.value?.user_role_id === 2);

const items = computed(() => [
  {
    key: 'practice',
    label: t('siteHeader.navPractice'),
    icon: 'pi pi-home',
    // The Start flow (docs/client-feedback-redesign.md §1).
    command: () => router.push('/practice'),
    path: '/practice',
  },
  {
    label: t('siteHeader.navProgress'),
    icon: 'pi pi-chart-line',
    command: () => router.push('/progress'),
    path: '/progress',
  },
  {
    label: t('siteHeader.navResources'),
    icon: 'pi pi-book',
    command: () => router.push('/resources'),
    path: '/resources',
  },
  {
    label: t('siteHeader.navContact'),
    icon: 'pi pi-envelope',
    command: () => router.push('/contact'),
    path: '/contact',
  },
  ...(isFaculty.value
    ? [
        {
          label: t('siteHeader.navAnalytics'),
          icon: 'pi pi-chart-bar',
          command: () => router.push('/analytics'),
          path: '/analytics',
        },
      ]
    : []),
]);

const handleAuth = () => {
  let redirectPath = '/';

  if (route.path === '/auth') {
    if (typeof route.query.redirect === 'string' && route.query.redirect) {
      redirectPath = route.query.redirect;
    } else {
      redirectPath = '/';
    }
  } else {
    redirectPath = route.fullPath;
  }
  router.push({ path: '/auth', query: { redirect: redirectPath } });
};

const handleLogout = async () => {
  try {
    // Import here to avoid circular dependency issues
    const { logout } = await import('@/api/auth');
    await logout();
    router.push('/');
  } catch (error) {
    console.error('[Header] Logout failed:', error);
    toast.add({
      severity: 'error',
      summary: t('siteHeader.logoutErrorTitle'),
      detail: t('siteHeader.logoutErrorBody'),
    });
  }
};
</script>
